import React, { useState, useEffect } from 'react';
import { FaTimes, FaTrash, FaImage } from 'react-icons/fa';
import api from '../../utils/api.service';
import { toast } from 'react-toastify';
import './ConfirmModal.css'; // Reuse existing modal styles

const EditPostModal = ({ isOpen, onClose, post, onSuccess }) => {
  const [content, setContent] = useState('');
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && post) {
      setContent(post.content || '');
      setExistingImages(post.mediaUrls || (post.mediaUrl ? [post.mediaUrl] : []));
      setNewImages([]);
    }
  }, [isOpen, post]);

  if (!isOpen || !post) return null;

  const handleRemoveExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.size <= 20 * 1024 * 1024); // 20MB limit
    
    if (validFiles.length !== files.length) {
      toast.warning('Some files were ignored because they exceed the 20MB limit.');
    }

    if (existingImages.length + newImages.length + validFiles.length > 10) {
      toast.error('You can only have up to 10 images per post.');
      return;
    }

    setNewImages(prev => [...prev, ...validFiles]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('content', content);

      // Append kept existing images
      if (existingImages.length === 0) {
        // To trick the backend into deleting all, pass an empty array (handled as empty string by FormData sometimes)
        formData.append('existingMediaUrls', ''); 
      } else {
        existingImages.forEach(url => {
          formData.append('existingMediaUrls', url);
        });
      }

      // Append new images
      newImages.forEach(file => {
        formData.append('images', file);
      });

      await api.patch(`/community/posts/${post.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Post updated successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to update post:', error);
      toast.error('Failed to update post.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="confirm-modal-overlay" onClick={onClose}>
      <div 
        className="confirm-modal-container" 
        style={{ maxWidth: '600px', textAlign: 'left' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>Edit Post</h2>
          <button 
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#334155' }}>
              Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '15px',
                resize: 'vertical',
                outline: 'none'
              }}
              placeholder="What's on your mind?"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#334155' }}>
              Images ({existingImages.length + newImages.length} / 10)
            </label>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
              {/* Existing Images */}
              {existingImages.map((url, index) => (
                <div key={`existing-${index}`} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                  <img src={url} alt={`Post img ${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button
                    type="button"
                    onClick={() => handleRemoveExistingImage(index)}
                    style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(255,0,0,0.8)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '10px' }}
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}

              {/* New Images */}
              {newImages.map((file, index) => (
                <div key={`new-${index}`} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #3b82f6' }}>
                  <img src={URL.createObjectURL(file)} alt={`New upload ${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button
                    type="button"
                    onClick={() => handleRemoveNewImage(index)}
                    style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(255,0,0,0.8)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '10px' }}
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}

              {/* Add New Image Button */}
              {existingImages.length + newImages.length < 10 && (
                <label style={{ width: '80px', height: '80px', borderRadius: '8px', border: '2px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b', background: '#f8fafc' }}>
                  <FaImage size={24} style={{ marginBottom: '4px' }} />
                  <span style={{ fontSize: '12px', fontWeight: '500' }}>Add</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageSelect}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '30px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="modal-btn modal-btn-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="modal-btn"
              style={{ background: '#3b82f6', color: 'white' }}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPostModal;
